/**
 * In-body cross-links between industry and service pages.
 * Anchor text is descriptive and names the target topic.
 */

export interface CrossLink {
  to: string;
  label: string;
  note: string;
}

/** For each industry detail page: the services that matter most to that trade. */
export const INDUSTRY_SERVICE_LINKS: Record<string, CrossLink[]> = {
  roofing: [
    {
      to: "/services/local-services-ads",
      label: "Local Services Ads management for roofers",
      note: "Google Verified placement, verification, and lead review for high-ticket roof jobs.",
    },
    {
      to: "/services/google-ads",
      label: "Google Ads for storm and retail roofing demand",
      note: "Search and Performance Max structured to ramp on storm events without overspending in quiet weeks.",
    },
    {
      to: "/services/meta-ads",
      label: "Meta Ads for roof inspection offers",
      note: "Creative tested against free-inspection and financing hooks rather than generic brand spots.",
    },
  ],
  painting: [
    {
      to: "/services/local-services-ads",
      label: "Local Services Ads for painting contractors",
      note: "Verified profile placement above Search, tuned for neighborhood-level job quality.",
    },
    {
      to: "/services/google-ads",
      label: "Google Ads for interior and exterior painting",
      note: "Seasonal exterior ramp, service-segmented Search, and booked-job conversion signals.",
    },
    {
      to: "/services/meta-ads",
      label: "Meta Ads creative for finish-quality proof",
      note: "Before/after and craftsmanship-led creative tested weekly against qualified inquiries.",
    },
  ],
  "pool-service": [
    {
      to: "/services/meta-ads",
      label: "Meta Ads for pool service plan acquisition",
      note: "Offer and hook testing built around recurring maintenance plans, not one-off cleanings.",
    },
    {
      to: "/services/google-ads",
      label: "Google Ads for pool repair and seasonal demand",
      note: "Search and PMax with LTV-based bid signals for recurring chemistry and service customers.",
    },
    {
      to: "/services/local-services-ads",
      label: "Local Services Ads for pool technicians",
      note: "Route-density geo targeting inside Google Verified placements.",
    },
  ],
  landscaping: [
    {
      to: "/services/google-ads",
      label: "Google Ads for design-build and maintenance",
      note: "Separate account structure so recurring maintenance and high-ticket installs stop competing.",
    },
    {
      to: "/services/meta-ads",
      label: "Meta Ads for landscape portfolio marketing",
      note: "Project-portfolio creative that sells the outcome instead of a discount.",
    },
    {
      to: "/services/youtube-ads",
      label: "YouTube Ads for design-build awareness",
      note: "Video that builds demand for large installs ahead of the seasonal buying window.",
    },
  ],
  "general-contractors": [
    {
      to: "/services/google-ads",
      label: "Google Ads for remodels, additions, and new builds",
      note: "Project-type segmentation plus CRM offline conversion imports for long sales cycles.",
    },
    {
      to: "/services/youtube-ads",
      label: "YouTube Ads for contractor brand authority",
      note: "Showcase video that keeps you present across a multi-week decision window.",
    },
    {
      to: "/services/meta-ads",
      label: "Meta Ads retargeting for long decision cycles",
      note: "Long-window retargeting tuned to how homeowners actually decide on major projects.",
    },
  ],
  "hardscape-drywall": [
    {
      to: "/services/google-ads",
      label: "Google Ads for hardscape and drywall projects",
      note: "Bid segmentation by project-value band so small repairs don't set the price for patios and walls.",
    },
    {
      to: "/services/local-services-ads",
      label: "Local Services Ads for repair-intent calls",
      note: "Google Verified placement for the fast, call-now half of the demand curve.",
    },
    {
      to: "/services/meta-ads",
      label: "Meta Ads for hardscape project showcases",
      note: "Patio and retaining-wall builds shown as finished work, tested against qualified inquiries.",
    },
  ],
};

/** For each service detail page: the industries where it does the most work. */
export const SERVICE_INDUSTRY_LINKS: Record<string, CrossLink[]> = {
  "google-ads": [
    {
      to: "/industries/roofing",
      label: "Google Ads for roofing companies",
      note: "Storm-cycle ramping and insurance vs. retail intent segmentation.",
    },
    {
      to: "/industries/general-contractors",
      label: "paid search for general contractors",
      note: "Offline conversion tracking built for weeks-long remodel and build cycles.",
    },
    {
      to: "/industries/pool-service",
      label: "pool service lead generation",
      note: "LTV-weighted bidding for recurring maintenance plans.",
    },
  ],
  "meta-ads": [
    {
      to: "/industries/pool-service",
      label: "Meta Ads for pool service companies",
      note: "Plan-offer creative tested weekly against booked-service events.",
    },
    {
      to: "/industries/painting",
      label: "paid social for painting contractors",
      note: "Finish-quality creative aimed at higher-ticket neighborhoods.",
    },
    {
      to: "/industries/landscaping",
      label: "landscaping design-build campaigns",
      note: "Portfolio-led creative for install work with real margin.",
    },
  ],
  "local-services-ads": [
    {
      to: "/industries/roofing",
      label: "Local Services Ads for roofing contractors",
      note: "Verification, badge maintenance, and lead review on high-ticket leads.",
    },
    {
      to: "/industries/painting",
      label: "painting contractor lead generation",
      note: "Neighborhood-level targeting inside Google Verified placement.",
    },
    {
      to: "/industries/hardscape-drywall",
      label: "hardscape and drywall repair demand",
      note: "Call-now intent captured above the standard Search results.",
    },
  ],
  "youtube-ads": [
    {
      to: "/industries/general-contractors",
      label: "YouTube Ads for general contractors",
      note: "Authority video across long, considered project decisions.",
    },
    {
      to: "/industries/landscaping",
      label: "landscaping design-build marketing",
      note: "Pre-season demand building for large installs.",
    },
    {
      to: "/industries/roofing",
      label: "roofing brand awareness campaigns",
      note: "Market presence before the storm event, not after competitors bid it up.",
    },
  ],
};
